import React, { useState, useEffect } from 'react';
import { 
  Package, ShoppingCart, Plus, Minus, Info, Sparkles, 
  Building, Shield, Eye, Database, TrendingUp, Award 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Import materials and options from the main calculator
import { materials } from '@/constants/materials';
import { additionalOptions } from '@/constants/options';

// Component for dimension inputs
const DimensionsInput = ({ dimensions, onChange, product }: any) => {
  return (
    <div className="grid grid-cols-3 gap-2">
      <input
        type="number"
        value={dimensions.width}
        onChange={(e) => onChange({ ...dimensions, width: parseInt(e.target.value) || 0 })}
        className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white"
        placeholder="Szerokość"
      />
      <input
        type="number"
        value={dimensions.height}
        onChange={(e) => onChange({ ...dimensions, height: parseInt(e.target.value) || 0 })}
        className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white"
        placeholder="Wysokość"
      />
      {dimensions.depth !== undefined && (
        <input
          type="number"
          value={dimensions.depth}
          onChange={(e) => onChange({ ...dimensions, depth: parseInt(e.target.value) || 0 })}
          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white"
          placeholder="Głębokość"
        />
      )}
    </div>
  );
};

const getColorClasses = (color: string) => {
  const colors: Record<string, string> = {
    violet: 'from-violet-500 to-purple-500',
    blue: 'from-blue-500 to-cyan-500',
    green: 'from-green-500 to-emerald-500',
    orange: 'from-orange-500 to-red-500',
    pink: 'from-pink-500 to-rose-500',
    yellow: 'from-yellow-500 to-orange-500',
    teal: 'from-teal-500 to-cyan-500',
    indigo: 'from-indigo-500 to-purple-500'
  };
  return colors[color] || colors.violet;
};

// Product definitions with REAL parameters from standard calculator
const PRODUCTS: any = {
  formatka: {
    name: 'Formatka',
    description: 'Cięcie na wymiar',
    icon: Package,
    color: 'violet',
    unit: 'szt',
    waste: 0.05,
    multiplier: 1.3,
    info: 'Odpad: 5% | Marża: ×1.3',
    parameters: {
      material: {
        label: 'Materiał',
        type: 'select',
        options: materials.map(m => ({
          value: m.id,
          name: m.name,
          price: m.basePrice
        })),
        default: 'plexi'
      },
      thickness: {
        label: 'Grubość',
        type: 'slider',
        min: 1,
        max: 30,
        step: 0.5,
        default: 3
      },
      wymiary: {
        label: 'Wymiary (mm)',
        type: 'dimensions',
        width: 1000,
        height: 1000
      }
    }
  },
  pojemnik: {
    name: 'Pojemnik',
    description: 'Z przegrodami i wiekiem',
    icon: Shield,
    color: 'blue',
    unit: 'szt',
    waste: 0.08,
    multiplier: 1.5,
    info: 'Odpad: 8% | Marża: ×1.5',
    parameters: {
      material: {
        label: 'Materiał',
        type: 'select',
        options: materials.map(m => ({
          value: m.id,
          name: m.name,
          price: m.basePrice
        })),
        default: 'plexi'
      },
      thickness: {
        label: 'Grubość',
        type: 'slider',
        min: 1,
        max: 10,
        step: 0.5,
        default: 3
      },
      wymiary: {
        label: 'Wymiary (mm)',
        type: 'dimensions',
        width: 300,
        height: 200,
        depth: 150
      },
      partitions: {
        label: 'Ilość przegród',
        type: 'number',
        min: 0,
        max: 10,
        default: 0
      },
      lid: {
        label: 'Wieko',
        type: 'select',
        options: [
          { value: 'Brak', addCost: 0 },
          { value: 'Zawiasy', addCost: 18 },
          { value: 'Zdejmowane', addCost: 0 }
        ],
        default: 'Brak'
      },
      bottom: {
        label: 'Dno',
        type: 'select',
        options: [
          { value: 'Plexi', addCost: 0 },
          { value: 'Dibond', addCost: 30 },
          { value: 'MDF', addCost: 20 }
        ],
        default: 'Plexi'
      }
    }
  },
  gablota: {
    name: 'Gablota',
    description: 'Z zamkiem i różnymi materiałami',
    icon: Eye,
    color: 'green',
    unit: 'szt',
    waste: 0.10,
    multiplier: 2.0,
    info: 'Odpad: 10% | Marża: ×2.0',
    parameters: {
      material: {
        label: 'Materiał frontu',
        type: 'select',
        options: materials.map(m => ({
          value: m.id,
          name: m.name,
          price: m.basePrice
        })),
        default: 'plexi'
      },
      thickness: {
        label: 'Grubość',
        type: 'slider',
        min: 1,
        max: 10,
        step: 0.5,
        default: 3
      },
      wymiary: {
        label: 'Wymiary (mm)',
        type: 'dimensions',
        width: 600,
        height: 800,
        depth: 100
      },
      backMaterial: {
        label: 'Tył',
        type: 'select',
        options: [
          { value: 'Dibond', price: 80 },
          { value: 'PCV 3mm', price: 54 },
          { value: 'MDF', price: 40 }
        ],
        default: 'Dibond'
      },
      lock: {
        label: 'Zamek',
        type: 'select',
        options: [
          { value: 'Brak', addCost: 0 },
          { value: 'Standardowy', addCost: 18 },
          { value: 'Premium', addCost: 45 }
        ],
        default: 'Standardowy'
      },
      shelves: {
        label: 'Ilość półek',
        type: 'number',
        min: 0,
        max: 10,
        default: 3
      }
    }
  },
  obudowa: {
    name: 'Obudowa',
    description: 'Z otworami wentylacyjnymi',
    icon: Building,
    color: 'orange',
    unit: 'szt',
    waste: 0.08,
    multiplier: 1.5,
    info: 'Odpad: 8% | Marża: ×1.5',
    parameters: {
      material: {
        label: 'Materiał',
        type: 'select',
        options: materials.map(m => ({
          value: m.id,
          name: m.name,
          price: m.basePrice
        })),
        default: 'plexi'
      },
      thickness: {
        label: 'Grubość',
        type: 'slider',
        min: 3,
        max: 10,
        step: 1,
        default: 5
      },
      wymiary: {
        label: 'Wymiary (mm)',
        type: 'dimensions',
        width: 400,
        height: 300,
        depth: 200
      },
      ventilationPercent: {
        label: 'Otwory wentylacyjne (%)',
        type: 'slider',
        min: 0,
        max: 30,
        step: 5,
        default: 10
      },
      includeTop: {
        label: 'Dodaj górę',
        type: 'checkbox',
        default: false
      }
    }
  },
  kaseton: {
    name: 'Kaseton świetlny',
    description: 'Z podświetleniem LED',
    icon: Sparkles,
    color: 'yellow',
    unit: 'szt',
    waste: 0,
    multiplier: 1,
    info: 'Cena stała za m²',
    parameters: {
      type: {
        label: 'Typ kasetonu',
        type: 'select',
        options: [
          { value: 'Jednostronny', price: 1550 },
          { value: 'Dwustronny', price: 1400 }
        ],
        default: 'Jednostronny'
      },
      wymiary: {
        label: 'Wymiary (mm)',
        type: 'dimensions',
        width: 1000,
        height: 500
      },
      letters: {
        label: 'Litery kasetonowe',
        type: 'select',
        options: [
          { value: 'Brak', addCost: 0 },
          { value: 'Do 20cm', addCost: 200 },
          { value: '20-50cm', addCost: 400 },
          { value: 'Powyżej 50cm', addCost: 500 }
        ],
        default: 'Brak'
      }
    }
  },
  ledon: {
    name: 'LED-ON',
    description: 'Panel świetlny wodoodporny',
    icon: TrendingUp,
    color: 'teal',
    unit: 'szt',
    waste: 0.05,
    multiplier: 1.4,
    info: 'Odpad: 5% | Marża: ×1.4',
    parameters: {
      material: {
        label: 'Materiał frontu',
        type: 'select',
        options: [
          { value: 'plexi_opal', name: 'Plexi 3mm opal', price: 99 },
          { value: 'plexi_opal_5', name: 'Plexi 5mm opal', price: 165 }
        ],
        default: 'plexi_opal'
      },
      wymiary: {
        label: 'Wymiary (mm)',
        type: 'dimensions',
        width: 600,
        height: 600
      },
      waterproof: {
        label: 'Wodoodporność',
        type: 'select',
        options: [
          { value: 'IP20', addCost: 0 },
          { value: 'IP44', addCost: 50 },
          { value: 'IP65', addCost: 100 }
        ],
        default: 'IP20'
      },
      ledLength: {
        label: 'Długość LED (cm)',
        type: 'number',
        min: 10,
        max: 500,
        default: 100
      }
    }
  },
  impuls: {
    name: 'Impuls',
    description: 'Stojak reklamowy z półkami',
    icon: Award,
    color: 'pink',
    unit: 'szt',
    waste: 0.08,
    multiplier: 1.6,
    info: 'Odpad: 8% | Marża: ×1.6',
    parameters: {
      material: {
        label: 'Materiał',
        type: 'select',
        options: materials.map(m => ({
          value: m.id,
          name: m.name,
          price: m.basePrice
        })),
        default: 'plexi'
      },
      thickness: {
        label: 'Grubość',
        type: 'slider',
        min: 3,
        max: 10,
        step: 1,
        default: 5
      },
      wymiary: {
        label: 'Wymiary podstawy (mm)',
        type: 'dimensions',
        width: 300,
        height: 400
      },
      shelves: {
        label: 'Ilość półek',
        type: 'number',
        min: 1,
        max: 6,
        default: 3
      },
      supports: {
        label: 'Wsporniki',
        type: 'select',
        options: [
          { value: 'Proste', addCost: 0 },
          { value: 'Wzmocnione', addCost: 30 },
          { value: 'Designerskie', addCost: 60 }
        ],
        default: 'Proste'
      }
    }
  },
  ekspozytor: {
    name: 'Ekspozytor',
    description: 'Stand reklamowy z kieszenią',
    icon: Database,
    color: 'indigo',
    unit: 'szt',
    waste: 0.08,
    multiplier: 1.8,
    info: 'Odpad: 8% | Marża: ×1.8',
    parameters: {
      type: {
        label: 'Typ ekspozytora',
        type: 'select',
        options: [
          { value: 'Stojący A4', description: 'Na ulotki' },
          { value: 'Stojący A5', description: 'Na foldery' },
          { value: 'Naladowy A4', description: 'Na ladę' },
          { value: 'Naladowy A5', description: 'Kompaktowy' },
          { value: 'Ścienny A4', description: 'Na ścianę' },
          { value: 'Obrotowy', description: '360°', addCost: 100 }
        ],
        default: 'Stojący A4'
      },
      material: {
        label: 'Materiał',
        type: 'select',
        options: materials.map(m => ({
          value: m.id,
          name: m.name,
          price: m.basePrice
        })),
        default: 'plexi'
      },
      thickness: {
        label: 'Grubość',
        type: 'slider',
        min: 3,
        max: 5,
        step: 1,
        default: 3
      },
      pockets: {
        label: 'Ilość kieszeni',
        type: 'number',
        min: 1,
        max: 10,
        default: 3
      },
      graphics: {
        label: 'Grafika',
        type: 'select',
        options: [
          { value: 'Brak', addCost: 0 },
          { value: 'Jednostronna', addCost: 75 },
          { value: 'Dwustronna', addCost: 150 }
        ],
        default: 'Brak'
      }
    }
  }
};

const CrystalCalculatorPage: React.FC = () => {
  const navigate = useNavigate();
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [productParams, setProductParams] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showStats, setShowStats] = useState(true);

  const handleQuantityChange = (product: string, delta: number) => {
    setQuantities(prev => ({
      ...prev,
      [product]: Math.max(0, (prev[product] || 0) + delta)
    }));
  };

  const handleParamChange = (product: string, param: string, value: any) => {
    setProductParams(prev => ({
      ...prev,
      [product]: {
        ...prev[product],
        [param]: value
      }
    }));
  };

  // Real calculation function from standard calculator
  const calculatePrice = (productKey: string) => {
    const quantity = quantities[productKey] || 0;
    if (quantity === 0) return 0;
    
    const product = PRODUCTS[productKey];
    const params = productParams[productKey] || {};
    const dimensions = params.wymiary || product.parameters.wymiary || {};
    
    // Calculate surface in m²
    const w = (dimensions.width || 1000) / 1000;
    const h = (dimensions.height || 1000) / 1000;
    const d = (dimensions.depth || 150) / 1000;
    
    let surface = 0;
    let materialCost = 0;
    let optionsCost = 0;
    
    // Surface calculation based on product type
    switch (productKey) {
      case 'formatka':
        surface = w * h;
        break;
        
      case 'pojemnik':
        const frontBack = 2 * (w * h);
        const sides = 2 * (d * h);
        let bottomArea = w * d;
        const lid = params.lid === 'Zawiasy' || params.lid === 'Zdejmowane' ? w * d : 0;
        const partitionArea = (params.partitions || 0) * h * d;
        
        if (params.bottom !== 'Plexi') {
          const bottomPrices: Record<string, number> = {
            'Dibond': 30,
            'MDF': 20
          };
          optionsCost += (bottomPrices[params.bottom] || 0) * bottomArea;
          bottomArea = 0;
        }
        
        surface = frontBack + sides + bottomArea + lid + partitionArea;
        break;
        
      case 'gablota':
        surface = 2 * (w * h) + 2 * (d * h) + w * d;
        break;
        
      case 'obudowa':
        let obudowaSurface = 2 * (w * h) + 2 * (d * h) + w * d;
        if (params.includeTop) {
          obudowaSurface += w * d;
        }
        if (params.ventilationPercent > 0) {
          obudowaSurface *= (1 - params.ventilationPercent / 100);
        }
        surface = obudowaSurface;
        break;
        
      case 'kaseton':
        surface = w * h;
        const type = params.type || 'Jednostronny';
        const basePrice = type === 'Jednostronny' ? 1550 : 1400;
        const lettersPrices: Record<string, number> = {
          'Do 20cm': 200,
          '20-50cm': 400,
          'Powyżej 50cm': 500
        };
        const lettersCost = params.letters && params.letters !== 'Brak' ? 
          (lettersPrices[params.letters] || 0) * surface : 0;
        
        return (basePrice * surface + lettersCost) * quantity;
        
      case 'ledon':
        surface = w * h;
        break;
        
      case 'impuls':
        const back = w * h;
        const impSides = 2 * (d * h);
        const shelvesArea = (params.shelves || 3) * w * d;
        const shelfSupports = (params.shelves || 3) * w * 0.05;
        surface = back + impSides + shelvesArea + shelfSupports;
        break;
        
      case 'ekspozytor':
        const expType = params.type || 'Stojący A4';
        if (expType.includes('A4')) {
          surface = 0.21 * 0.297;
        } else if (expType.includes('A5')) {
          surface = 0.148 * 0.21;
        } else {
          surface = w * h * 1.5;
        }
        surface += (params.pockets || 3) * 0.01;
        
        if (expType === 'Obrotowy') {
          optionsCost += 100;
        }
        if (params.graphics === 'Jednostronna') {
          optionsCost += 75;
        } else if (params.graphics === 'Dwustronna') {
          optionsCost += 150;
        }
        break;
        
      default:
        surface = w * h;
    }
    
    // Calculate material cost
    if (productKey !== 'kaseton') {
      const selectedMaterial = params.material || product.parameters.material?.default || 'plexi';
      const material = materials.find(m => m.id === selectedMaterial);
      const thickness = params.thickness || product.parameters.thickness?.default || 3;
      
      if (material) {
        const surfaceWithWaste = surface * (1 + product.waste);
        
        // Special handling for colored plexi
        if (material.pricePerThickness) {
          const pricePerM2 = material.pricePerThickness[thickness] || material.basePrice * thickness;
          materialCost = pricePerM2 * surfaceWithWaste;
        } else {
          materialCost = material.basePrice * thickness * surfaceWithWaste;
        }
        
        // Add labor cost (33.3%)
        const laborCost = materialCost * 0.333;
        
        // Apply multiplier
        const unitPrice = (materialCost + laborCost) * product.multiplier + optionsCost;
        
        return unitPrice * quantity;
      }
    }
    
    return 0;
  };

  const getPriceBreakdown = (productKey: string) => {
    const quantity = quantities[productKey] || 0;
    if (quantity === 0) return null;
    
    const totalPrice = calculatePrice(productKey);
    const unitPrice = totalPrice / quantity;
    const product = PRODUCTS[productKey];
    
    return {
      calculation: 'Powierzchnia × cena materiału × (1 + odpad) × (1 + robocizna) × marża',
      unitPrice: unitPrice,
      totalPrice: totalPrice,
      waste: product.waste,
      multiplier: product.multiplier
    };
  };

  const totalPrice = Object.keys(quantities).reduce((sum, key) => {
    return sum + calculatePrice(key);
  }, 0);

  const handleAddToOffer = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      navigate('/offer/new');
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">Crystal Calculator PRO</h1>
        <p className="text-gray-400">Profesjonalna wycena produktów z pleksi</p>
      </div>

      {/* Stats */}
      {showStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/30">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-violet-400" />
              <span className="text-xs text-gray-400">Dziś</span>
            </div>
            <p className="text-lg font-bold text-violet-400">12</p>
            <p className="text-xs text-gray-500">wycen</p>
          </div>
          
          <div className="p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-green-400" />
              <span className="text-xs text-gray-400">Miesiąc</span>
            </div>
            <p className="text-lg font-bold text-green-400">284</p>
            <p className="text-xs text-gray-500">wykonanych</p>
          </div>
          
          <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-gray-400">W bazie</span>
            </div>
            <p className="text-lg font-bold text-blue-400">250+</p>
            <p className="text-xs text-gray-500">produktów</p>
          </div>
        </div>
      )}

      {/* Cart Summary */}
      <div className="sticky top-20 z-30 p-4 rounded-2xl bg-black/60 backdrop-blur-xl border border-violet-500/20 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-6 h-6 text-violet-400" />
            <span className="text-lg font-medium text-white">Podsumowanie wyceny</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-3xl font-bold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
                {totalPrice.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
              </p>
              <p className="text-sm text-gray-400">netto (bez VAT)</p>
            </div>
            <button 
              onClick={handleAddToOffer}
              disabled={totalPrice === 0 || isLoading}
              className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${
                totalPrice > 0 
                  ? 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white hover:scale-[1.02]' 
                  : 'bg-gray-800 text-gray-500 cursor-not-allowed'
              }`}
            >
              <ShoppingCart className="w-5 h-5" />
              {isLoading ? 'Dodawanie...' : 'Dodaj do oferty'}
            </button>
          </div>
        </div>
        
        {/* Quick summary of selected items */}
        {Object.entries(quantities).filter(([_, qty]) => qty > 0).length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-sm text-gray-400 mb-2">Wybrane produkty:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(quantities).filter(([_, qty]) => qty > 0).map(([key, qty]) => {
                const breakdown = getPriceBreakdown(key);
                return (
                  <div key={key} className="px-3 py-1 bg-white/5 rounded-lg text-sm">
                    <span className="text-white font-medium">{PRODUCTS[key].name}</span>
                    <span className="text-gray-400 ml-2">× {qty}</span>
                    {breakdown && (
                      <span className="text-violet-400 ml-2">
                        = {breakdown.totalPrice.toFixed(2)} zł
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Product Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(PRODUCTS).map(([key, product]) => {
          const IconComponent = product.icon;
          const quantity = quantities[key] || 0;
          const params = productParams[key] || {};
          const price = calculatePrice(key);
          const breakdown = getPriceBreakdown(key);
          
          return (
            <div key={key} className="group relative">
              {/* Glow Effect */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${getColorClasses(product.color)} 
                opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500`} />
              
              {/* Card */}
              <div className="relative rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 hover:border-violet-500/30 transition-all duration-500">
                {/* Header */}
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${getColorClasses(product.color)} bg-opacity-20`}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-white">{product.name}</h3>
                        <p className="text-sm text-gray-400">{product.description}</p>
                      </div>
                    </div>
                    {quantity > 0 && (
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-yellow-400 animate-spin" />
                        <span className="text-sm font-bold text-yellow-400">{quantity}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Info box */}
                  <div className="mt-3 p-2 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <p className="text-xs text-blue-400">{product.info}</p>
                  </div>
                </div>

                {/* Parameters */}
                <div className="p-6 space-y-4">
                  {Object.entries(product.parameters).map(([paramKey, param]: [string, any]) => {
                    return (
                      <div key={paramKey}>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {param.label}
                        </label>
                        {param.type === 'dimensions' ? (
                          <DimensionsInput
                            dimensions={params[paramKey] || { 
                              width: param.width || 1000, 
                              height: param.height || 1000,
                              depth: param.depth
                            }}
                            onChange={(value: any) => handleParamChange(key, paramKey, value)}
                            product={key}
                          />
                        ) : param.type === 'number' ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleParamChange(key, paramKey, Math.max(param.min, (params[paramKey] || param.default) - 1))}
                              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <input
                              type="number"
                              value={params[paramKey] || param.default}
                              onChange={(e) => handleParamChange(key, paramKey, Math.min(param.max, Math.max(param.min, parseInt(e.target.value) || param.min)))}
                              className="flex-1 px-3 py-2 text-center rounded-lg bg-white/5 border border-white/10 text-white font-mono"
                              min={param.min}
                              max={param.max}
                            />
                            <button
                              onClick={() => handleParamChange(key, paramKey, Math.min(param.max, (params[paramKey] || param.default) + 1))}
                              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        ) : param.type === 'slider' ? (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-400">{param.min}{param.label.includes('%') ? '%' : 'mm'}</span>
                              <span className="text-sm font-bold text-violet-400">
                                {params[paramKey] || param.default}{param.label.includes('%') ? '%' : 'mm'}
                              </span>
                              <span className="text-xs text-gray-400">{param.max}{param.label.includes('%') ? '%' : 'mm'}</span>
                            </div>
                            <input
                              type="range"
                              min={param.min}
                              max={param.max}
                              step={param.step}
                              value={params[paramKey] || param.default}
                              onChange={(e) => handleParamChange(key, paramKey, Number(e.target.value))}
                              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-violet-500"
                            />
                          </div>
                        ) : param.type === 'checkbox' ? (
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={params[paramKey] || param.default}
                              onChange={(e) => handleParamChange(key, paramKey, e.target.checked)}
                              className="w-4 h-4 rounded border-white/20 bg-white/5 text-violet-500"
                            />
                            <span className="text-sm text-gray-300">Zaznacz aby dodać</span>
                          </label>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {param.options.map((option: any) => (
                              <button
                                key={option.value}
                                onClick={() => handleParamChange(key, paramKey, option.value)}
                                className={`px-3 py-2 rounded-lg text-sm transition-all ${
                                  (params[paramKey] || param.default) === option.value
                                    ? 'bg-gradient-to-r ' + getColorClasses(product.color) + ' text-white border border-transparent'
                                    : 'bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white'
                                }`}
                              >
                                <span className="block font-medium">{option.value}</span>
                                {option.description && (
                                  <span className="block text-xs mt-0.5 opacity-75">
                                    {option.description}
                                  </span>
                                )}
                                {option.addCost && option.addCost > 0 && (
                                  <span className="block text-xs mt-0.5 text-yellow-400">
                                    +{option.addCost} zł
                                  </span>
                                )}
                                {option.price && (
                                  <span className="block text-xs mt-0.5 opacity-75">
                                    {option.price} zł/m²
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Quantity Selector */}
                  <div className="pt-4 border-t border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">Ilość</span>
                      <span className="text-lg font-bold text-white">
                        {product.unit}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleQuantityChange(key, -1)}
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-white hover:text-white"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantities(prev => ({
                          ...prev,
                          [key]: Math.max(0, parseInt(e.target.value) || 0)
                        }))}
                        className="flex-1 px-4 py-2 text-center rounded-lg bg-white/5 border border-white/10 text-white font-bold text-lg focus:outline-none focus:border-violet-500/50"
                      />
                      
                      <button
                        onClick={() => handleQuantityChange(key, 1)}
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-white hover:text-white"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Price Display with Breakdown */}
                    {quantity > 0 && breakdown && (
                      <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/30">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-400">
                            Cena za {quantity} {product.unit}
                          </span>
                          <span className="text-xl font-bold text-violet-400">
                            {price.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                          </span>
                        </div>
                        
                        {/* Calculation details */}
                        <div className="mt-2 pt-2 border-t border-violet-500/20">
                          <p className="text-xs text-gray-500">Kalkulacja:</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {breakdown.calculation}
                          </p>
                          {breakdown.unitPrice && (
                            <p className="text-xs text-gray-400 mt-1">
                              Cena jednostkowa: <span className="text-violet-400 font-bold">
                                {breakdown.unitPrice.toFixed(2)} zł/{product.unit}
                              </span>
                            </p>
                          )}
                          {key !== 'kaseton' && (
                            <p className="text-xs text-gray-400 mt-1">
                              Odpad: {(breakdown.waste * 100).toFixed(0)}% | Marża: ×{breakdown.multiplier}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Section */}
      <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-blue-500/10 to-violet-500/10 border border-blue-500/30">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <h3 className="font-bold text-white mb-2">✅ Pełna integracja z kalkulatorem Standard</h3>
            <p className="text-sm text-gray-300 mb-2">
              Crystal Calculator PRO używa <strong>IDENTYCZNYCH</strong> materiałów, grubości, opcji i wzorów kalkulacji jak standardowy kalkulator PlexiSystem.
            </p>
            
            <div className="mt-4 p-3 bg-black/40 rounded-lg">
              <h4 className="text-sm font-bold text-white mb-2">Dostępne materiały ({materials.length}):</h4>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                {materials.slice(0, 8).map((m: any) => (
                  <div key={m.id}>
                    • <strong>{m.name}:</strong> {m.basePrice} zł/m²{!m.fixedThickness && '/mm'}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-green-500/20 rounded-lg border border-green-500/40">
              <h4 className="text-sm font-bold text-white mb-2">✅ Wzór kalkulacji (identyczny ze Standard):</h4>
              <ol className="text-xs text-gray-300 space-y-1">
                <li>1. Powierzchnia z odpadem = powierzchnia × (1 + % odpadu)</li>
                <li>2. Koszt materiału = cena bazowa × grubość × powierzchnia z odpadem</li>
                <li>3. Robocizna = koszt materiału × 33.3%</li>
                <li>4. Cena przed marżą = koszt materiału + robocizna</li>
                <li>5. Cena jednostkowa = cena przed marżą × marża produktu</li>
                <li>6. Cena końcowa = (cena jednostkowa + opcje dodatkowe) × ilość</li>
              </ol>
            </div>
            
            <p className="text-sm text-yellow-400 mt-4">
              <strong>✨ NOWOŚĆ:</strong> Pełny wybór wszystkich materiałów, grubości 1-30mm, wszystkie opcje dodatkowe - dokładnie jak w kalkulatorze Standard!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrystalCalculatorPage;